import * as React from 'react';
import axios from '../../utils/axios';
import { fDateSimple } from '../../utils/formatTime';
import GoogleMap from '../GoogleMaps/GoogleMap';
import GoogleMarker from '../GoogleMaps/GoogleMarker';
import TrajetoMapa from './TrajetoMapa';
import { Num } from './index';
import Modal from './Modal';
import { cn } from '../../lib/utils';

/* ============================================================================
   Geolocalizacao do dispositivo - visual novo.

   Substitui pages/dashboard/dispositivos/components/modal/modal-grafico.tsx
   (Dialog do MUI). SOMENTE LEITURA - um GET - por isso foi um dos primeiros da
   fila dos modais antigos.

   Contrato, o mesmo de antes:
     GET /dispositivos/paginadoLog?page=0&rows=100&deviceId=..&ordenar=CREATED_AT&direcao=desc

   E o mesmo comportamento: a lista traz so os registros COM latitude, e clicar
   num deles move o mapa para aquele ponto.

   TRES COISAS QUE MUDARAM, com motivo:

   1. O PONTO ATIVO FICA MARCADO. Antes dava para clicar em varios enderecos e
      nao havia como saber qual estava no mapa - em 100 registros parecidos isso
      se perde na primeira rolagem.

   2. CADA LINHA MOSTRA O TEMPO RELATIVO junto da data. "3 d" responde antes de
      ler a data se aquele rastro e de agora ou da semana passada.

   3. O MARCADOR NAO E MAIS ARRASTAVEL. No modal antigo ele tinha `draggable`, e
      arrastar mexia em `lat`/`lng` do estado - ou seja, dava para mover o pino e
      "ver" o aparelho num lugar onde ele nunca esteve. Isto e tela de consulta de
      rastro; nada aqui grava posicao. (A tela de detalhe do dispositivo mantem o
      marcador arrastavel porque lá ele existe junto do endereco editavel.)
   ============================================================================ */

type Log = {
  codigo?: number | string;
  latitude?: string | number;
  longitude?: string | number;
  endereco?: string;
  createdAt?: string;
  bateria?: number | string;
};

function tempoRelativo(valor?: string) {
  if (!valor) return '';
  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return '';
  const horas = (Date.now() - d.getTime()) / 36e5;
  if (horas < 1) return 'agora';
  if (horas < 24) return `${Math.floor(horas)} h`;
  return `${Math.floor(horas / 24)} d`;
}

export default function ModalGeolocalizacao({
  aberto,
  device,
  onFechar,
}: {
  aberto: boolean;
  device: { deviceId?: string; nome?: string } | null;
  onFechar: () => void;
}) {
  const [logs, setLogs] = React.useState<Log[]>([]);
  const [carregando, setCarregando] = React.useState(false);
  const [falhou, setFalhou] = React.useState(false);
  const [ativo, setAtivo] = React.useState<string | null>(null);

  const [lat, setLat] = React.useState<any>(null);
  const [lng, setLng] = React.useState<any>(null);
  const [zoom, setZoom] = React.useState(17);
  const [endereco, setEndereco] = React.useState('');

  /* TRAJETO (10/09/2026, a pedido).

     Comeca LIGADO: quem abre a geolocalizacao de um aparelho com cem registros
     quer ver por onde ele andou - o pino unico responde "onde esta agora", que
     e a pergunta menor e ja esta no cartao da tela de detalhe. Da para
     desligar, e ai volta o pino do ponto selecionado. */
  const [verTrajeto, setVerTrajeto] = React.useState(true);
  const [reproduzindo, setReproduzindo] = React.useState(false);
  const [velocidade, setVelocidade] = React.useState(1);

  React.useEffect(() => {
    if (!aberto || !device?.deviceId) return;
    let vivo = true;
    setFalhou(false);

    setCarregando(true);
    setLogs([]);
    setAtivo(null);
    setLat(null);
    setLng(null);
    setReproduzindo(false);

    axios
      .get<{ content: Log[]; totalElements: number }>(
        `/dispositivos/paginadoLog?page=0&rows=100&deviceId=${encodeURIComponent(device.deviceId)}` +
          `&ordenar=CREATED_AT&direcao=desc`
      )
      .then((r) => {
        if (!vivo) return;
        // So os registros COM latitude - mesma regra do modal antigo. Log sem
        // coordenada nao tem o que mostrar no mapa.
        const comPosicao = (r.data?.content ?? []).filter((l) => l.latitude);
        setLogs(comPosicao);

        if (comPosicao.length) {
          const primeiro = comPosicao[0];
          setLat(Number(primeiro.latitude));
          setLng(Number(primeiro.longitude));
          setAtivo(String(primeiro.codigo ?? 0));
        }
      })
      .catch(() => {
        /* ERRO NAO E "NAO TEM LOCALIZACAO" (28/09/2026).

           Zerar os logs aqui levava a tela a afirmar "Sem localizacao no
           dispositivo" - uma afirmacao de FATO sobre o aparelho, apresentada
           como resultado normal, quando na verdade a consulta falhou. Esta tela
           e usada em perda e roubo: dizer que nao ha rastro quando nao se sabe
           e o pior erro possivel aqui. */
        if (!vivo) return;
        setLogs([]);
        setFalhou(true);
      })
      .finally(() => vivo && setCarregando(false));

    return () => {
      vivo = false;
    };
  }, [aberto, device?.deviceId]);

  function irPara(log: Log) {
    setLat(Number(log.latitude));
    setLng(Number(log.longitude));
    setAtivo(String(log.codigo ?? 0));
  }

  const temPosicao = Boolean(lat && lng);

  /* Coordenada em texto no banco: `Number` de string vazia da 0, e 0,0 e um
     ponto valido no Golfo da Guine - o trajeto sairia de la. Por isso o filtro
     por `Number.isFinite` mais o teste de zero, e nao so `filter(l.latitude)`. */
  const pontos = React.useMemo(
    () =>
      logs
        .map((l) => ({
          lat: Number(l.latitude),
          lng: Number(l.longitude),
          quando: l.createdAt,
          endereco: l.endereco,
          chave: String(l.codigo ?? ''),
        }))
        .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng) && (p.lat !== 0 || p.lng !== 0)),
    [logs]
  );

  const mostrarTrajeto = verTrajeto && pontos.length > 1;

  /* Centro geometrico da rota. O `fitBounds` do TrajetoMapa e quem decide o
     zoom de verdade; isto so evita que a reaplicacao das opcoes puxe o mapa
     para outro lugar antes disso. `zoomTrajeto` fica num valor de cidade -
     valor estavel importa mais que valor certo, porque o certo vem do
     fitBounds logo em seguida. */
  const centroTrajeto = React.useMemo(() => {
    if (!pontos.length) return { lat: Number(lat) || 0, lng: Number(lng) || 0 };
    const soma = pontos.reduce((a, p) => ({ lat: a.lat + p.lat, lng: a.lng + p.lng }), { lat: 0, lng: 0 });
    return { lat: soma.lat / pontos.length, lng: soma.lng / pontos.length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pontos]);
  const zoomTrajeto = 13;

  return (
    <Modal
      aberto={aberto}
      titulo="Geolocalização"
      descricao={device ? `${device.nome ?? ''} · ${device.deviceId ?? ''}` : undefined}
      icone="◎"
      /* MAIOR (10/09/2026, a pedido, depois que o trajeto entrou).

         1000px servia para um pino: mapa pequeno, lista ao lado, fim. Com o
         trajeto o conteudo mudou de natureza - agora ha uma rota inteira para
         enquadrar, e num percurso urbano de cem pontos o `fitBounds` reduz
         tudo ate caber, deixando as ruas ilegiveis. Mapa maior nao e conforto
         aqui, e o que torna o recurso utilizavel. */
      largura="max-w-[1400px]"
      onFechar={onFechar}
    >
      <div className="grid grid-cols-1 items-stretch gap-3 lg:grid-cols-[1.9fr_1fr]">
        <div>
          {carregando ? (
            <div className="flex h-[620px] items-center justify-center rounded-lg border border-line bg-surface-2 text-[12.5px] text-muted-foreground">
              Carregando…
            </div>
          ) : falhou ? (
            <div className="flex h-[620px] flex-col items-center justify-center gap-1 rounded-lg border border-warn bg-warn-soft px-6 text-center text-[12.5px] text-warn">
              <span className="font-bold">Não foi possível consultar a localização.</span>
              <span className="text-[11.5px]">
                Isto não quer dizer que o aparelho esteja sem registro — a consulta falhou.
                Tente de novo em instantes.
              </span>
            </div>
          ) : !temPosicao ? (
            <div className="flex h-[620px] items-center justify-center rounded-lg border border-line bg-surface-2 text-[12.5px] text-muted-foreground">
              Sem localização no dispositivo
            </div>
          ) : (
            <>
            <div className="overflow-hidden rounded-lg border border-line">
              {/* CENTRO FIXO ENQUANTO O TRAJETO ESTA NA TELA.

                  O GoogleMap roda `map.setOptions(options)` a CADA render (o
                  objeto de opcoes e novo toda vez), e isso reaplica `center` e
                  `zoom`. Com o trajeto ligado, quem manda no enquadramento e o
                  `fitBounds` do TrajetoMapa - e qualquer render seguinte
                  (clicar num ponto da lista, por exemplo) jogava o mapa de
                  volta para o zoom de um ponto so, deixando a rota desenhada
                  fora da area visivel.

                  Passando o CENTRO DA ROTA e um zoom estavel, reaplicar as
                  opcoes deixa de brigar com o enquadramento. Com o trajeto
                  desligado o comportamento antigo volta inteiro: o mapa segue
                  o ponto selecionado. */}
              <GoogleMap
                center={mostrarTrajeto ? centroTrajeto : { lat, lng }}
                zoom={mostrarTrajeto ? zoomTrajeto : zoom}
                setZoom={setZoom}
                style={{ width: '100%', height: '620px' }}
                disableDefaultUI
                clickableIcons={false}
              >
                {/* UM filho so, e nao os dois: o GoogleMap clona o filho
                    injetando `map` (React.Children.map), e um fragmento com
                    dois elementos receberia a prop no fragmento - que a
                    descarta - em vez de nos componentes. */}
                {mostrarTrajeto ? (
                  <TrajetoMapa
                    pontos={pontos}
                    ativo={ativo}
                    aoClicarPonto={(chave) => setAtivo(chave)}
                    reproduzindo={reproduzindo}
                    velocidade={velocidade}
                    aoTerminar={() => setReproduzindo(false)}
                  />
                ) : (
                  /* Sem `draggable`: aqui e consulta de rastro, e arrastar o
                     pino mostrava o aparelho onde ele nunca esteve. */
                  <GoogleMarker position={{ lat, lng }} setLat={setLat} setLng={setLng} setAddress={setEndereco} />
                )}
              </GoogleMap>
            </div>

            {/* CONTROLES embaixo do mapa, nao em cima: sobre o mapa eles
                cobririam justamente o canto onde o trajeto costuma comecar. */}
            {pontos.length > 1 && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setVerTrajeto((v) => !v);
                    setReproduzindo(false);
                  }}
                  className={cn(
                    'inline-flex h-7 items-center gap-1.5 rounded-md border px-2 text-[11px] font-semibold',
                    verTrajeto ? 'border-primary bg-primary text-primary-foreground' : 'border-line text-muted-foreground hover:text-ink'
                  )}
                >
                  <svg width="12" height="12" viewBox="0 0 18 18" fill="none" aria-hidden>
                    <path d="M3 14c3-6 5 2 8-4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                    <circle cx="3" cy="14" r="1.8" fill="currentColor" />
                    <circle cx="14.5" cy="6.5" r="1.8" fill="currentColor" />
                  </svg>
                  Ver trajeto
                </button>

                <button
                  type="button"
                  disabled={!verTrajeto}
                  onClick={() => setReproduzindo((r) => !r)}
                  className="inline-flex h-7 items-center gap-1.5 rounded-md border border-line px-2 text-[11px] font-semibold text-muted-foreground hover:text-ink disabled:opacity-40"
                  title={verTrajeto ? undefined : 'Ligue "Ver trajeto" para percorrer a rota'}
                >
                  {reproduzindo ? (
                    <>
                      <svg width="11" height="11" viewBox="0 0 18 18" aria-hidden>
                        <rect x="4" y="3.5" width="3.5" height="11" rx="1" fill="currentColor" />
                        <rect x="10.5" y="3.5" width="3.5" height="11" rx="1" fill="currentColor" />
                      </svg>
                      Parar
                    </>
                  ) : (
                    <>
                      <svg width="11" height="11" viewBox="0 0 18 18" aria-hidden>
                        <path d="M4.5 3.5l10 5.5-10 5.5V3.5z" fill="currentColor" />
                      </svg>
                      Percorrer rota
                    </>
                  )}
                </button>

                <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  Velocidade
                  <select
                    className="h-7 rounded-md border border-line bg-surface px-1.5 text-[11px]"
                    value={velocidade}
                    onChange={(e) => setVelocidade(Number(e.target.value))}
                  >
                    {[0.5, 1, 2, 4].map((v) => (
                      <option key={v} value={v}>
                        {v}x
                      </option>
                    ))}
                  </select>
                </label>

                <span className="text-[11px] text-faint">
                  <Num className="font-semibold text-muted-foreground">{pontos.length}</Num> pontos no trajeto
                </span>
              </div>
            )}
            </>
          )}
        </div>

        {/* AS DUAS COLUNAS COM A MESMA ALTURA (10/09/2026, a pedido).

            O `max-h-[400px]` da lista foi dimensionado para o mapa de 440px.
            Quando o mapa cresceu para 620px, a lista parou nos 400 e sobrou um
            retangulo branco de quase 300px ao lado do mapa - a janela ficou
            maior e mais vazia ao mesmo tempo.

            Agora a lista acompanha a coluna do mapa: `items-stretch` na grade
            faz as duas terem a mesma altura, e `min-h-0` no bloco que rola e o
            que permite ele encolher dentro do flex - sem isso o filho com
            `overflow-y-auto` cresce ate o conteudo e empurra o cartao para
            fora. Nada de altura fixa repetida em dois lugares: um numero so, o
            do mapa, manda nos dois. */}
        <div className="flex max-h-[620px] flex-col overflow-hidden rounded-lg border border-line">
          <div className="flex items-center justify-between border-b border-line bg-surface-2 px-3 py-2">
            <span className="text-[11px] font-bold uppercase tracking-[0.07em] text-faint">Rastro</span>
            <Num className="text-[11px] text-muted-foreground">{logs.length}</Num>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {!carregando && !logs.length && (
              <div className="p-6 text-center text-[12px] text-muted-foreground">
                Nenhum registro com coordenada.
              </div>
            )}

            {logs.map((log) => {
              const selecionado = ativo === String(log.codigo ?? 0);
              return (
                <button
                  key={String(log.codigo)}
                  type="button"
                  onClick={() => irPara(log)}
                  className={cn(
                    'block w-full border-b border-line px-3 py-2 text-left last:border-b-0 hover:bg-surface-2',
                    // Ponto ativo marcado: sem isso nao havia como saber qual
                    // dos 100 registros estava no mapa.
                    selecionado && 'border-l-2 border-l-primary bg-primary/10'
                  )}
                >
                  <span className="flex items-baseline justify-between gap-2">
                    <Num className="text-[12px] font-bold">{fDateSimple(log.createdAt)}</Num>
                    <span className="flex-none text-[11px] text-faint">{tempoRelativo(log.createdAt)}</span>
                  </span>
                  <span className="mt-0.5 block text-[11px] text-muted-foreground">
                    {log.endereco || '—'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </Modal>
  );
}
